import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Building } from '@map/shared';
import { addFloors } from '../world/api.js';

/**
 * One project, shown as its site. The only control is "another one done",
 * because the whole point is that progress costs a single tap and everything
 * else — the height, the crew, the weeds — follows from it.
 */
export function BuildingCard({ building }: { building: Building }) {
  const queryClient = useQueryClient();

  const progress = useMutation({
    mutationFn: () => addFloors(building.id, 1),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['buildings', building.regionId] });
      await queryClient.invalidateQueries({ queryKey: ['planet-buildings'] });
      await queryClient.invalidateQueries({ queryKey: ['tree'] });
    },
  });

  const done = building.floorsDone;
  const finished = done >= building.floorsTotal;

  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-sm">{building.name}</h4>
        <span className="text-xs text-text-muted">
          {done} of {building.floorsTotal} {building.unit}
          {building.floorsTotal === 1 ? '' : 's'}
        </span>
      </div>

      {/* A bar, not a picture: the building itself is standing on the map. */}
      <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: 'var(--surface-2)' }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.round((done / building.floorsTotal) * 100)}%`,
            background: 'var(--accent, #6aa6d6)',
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-text-muted">{siteState(building)}</span>
        {!finished && (
          <button
            onClick={() => progress.mutate()}
            disabled={progress.isPending}
            className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1"
          >
            Another {building.unit} done
          </button>
        )}
      </div>
    </div>
  );
}

/** Said in terms of showing up, never in terms of how far there is to go. */
function siteState(building: Building): string {
  if (building.floorsDone >= building.floorsTotal) return 'Finished. It stands.';
  if (building.quietDays === null) return 'Not started — the site is open.';
  if (building.quietDays === 0) return 'Worked on today.';
  if (building.weathering === 0) return `Last worked on ${building.quietDays}d ago.`;
  if (building.weathering < 0.5) return `Quiet for ${building.quietDays} days.`;
  return `Abandoned for ${building.quietDays} days.`;
}
